
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Trash2, Calendar, User, Clock, MapPin, DollarSign } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';

const ItineraryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Fetch itinerary data
  const { data: itinerary, isLoading, error } = useQuery({
    queryKey: ['itinerary', id],
    queryFn: async () => {
      // First fetch the itinerary
      const { data: itineraryData, error: itineraryError } = await supabase
        .from('itineraries')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (itineraryError) throw itineraryError;
      if (!itineraryData) throw new Error('Itinerary not found');

      // Then fetch the author details if there's an author_id
      let authorData = null;
      if (itineraryData.author_id) {
        const { data: author, error: authorError } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', itineraryData.author_id)
          .maybeSingle();
        
        if (!authorError && author) {
          authorData = author;
        }
      }

      // Fetch itinerary days
      const { data: daysData, error: daysError } = await supabase
        .from('itinerary_days')
        .select('*')
        .eq('itinerary_id', id)
        .order('day_number', { ascending: true });

      const days = daysError ? [] : daysData;

      // Return combined data
      return {
        ...itineraryData,
        author: authorData,
        itinerary_days: days
      };
    },
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  // Delete handler
  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('itineraries')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Itinerary deleted successfully');
      navigate('/dashboard/itineraries');
    } catch (error) {
      console.error('Error deleting itinerary:', error);
      toast.error(`Failed to delete: ${error.message}`);
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  // Loading and error states
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="glass-card">
        <CardContent className="flex flex-col items-center justify-center h-64">
          <p className="text-destructive text-lg mb-4">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
          <Button onClick={() => navigate('/dashboard/itineraries')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Itineraries
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => navigate('/dashboard/itineraries')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="ml-auto flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate(`/dashboard/itineraries/${id}/edit`)}
          >
            <Pencil className="h-4 w-4 mr-2" /> Edit
          </Button>
          <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Are you sure?</DialogTitle>
                <DialogDescription>
                  This action cannot be undone. This will permanently delete the itinerary.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="glass-card overflow-hidden">
        {itinerary?.featured_image && (
          <div className="w-full h-64 overflow-hidden">
            <img 
              src={itinerary.featured_image} 
              alt={itinerary.title} 
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <CardHeader>
          <div className="flex flex-wrap gap-2 mb-2">
            <Badge variant={itinerary?.status === 'published' ? 'default' : 'outline'}>
              {itinerary?.status}
            </Badge>
          </div>
          <CardTitle className="text-3xl">{itinerary?.title}</CardTitle>
          {itinerary?.description && (
            <CardDescription className="text-lg mt-2">
              {itinerary.description}
            </CardDescription>
          )}
          <div className="flex flex-wrap gap-4 text-muted-foreground text-sm mt-2">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {formatDate(itinerary?.created_at)}
            </div>
            <div className="flex items-center">
              <User className="h-4 w-4 mr-1" />
              {itinerary?.author 
                ? `${itinerary.author.first_name || ''} ${itinerary.author.last_name || ''}` 
                : 'Unknown Author'}
            </div>
            {itinerary?.destination && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                {itinerary.destination}
              </div>
            )}
            {itinerary?.duration && (
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {itinerary.duration} {itinerary.duration === 1 ? 'day' : 'days'}
              </div>
            )}
            {itinerary?.cost_estimate && (
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 mr-1" />
                ${itinerary.cost_estimate}
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Itinerary Days */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Day-by-Day Itinerary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {itinerary?.itinerary_days && itinerary.itinerary_days.length > 0 ? (
            itinerary.itinerary_days.map((day, index) => (
              <div key={day.id} className="space-y-2">
                {index > 0 && <Separator className="my-4" />}
                <div className="flex items-center gap-2">
                  <Badge className="w-12 flex justify-center">Day {day.day_number}</Badge>
                  <h3 className="text-xl font-semibold">{day.title}</h3>
                </div>
                {day.location && (
                  <div className="flex items-center text-muted-foreground text-sm">
                    <MapPin className="h-4 w-4 mr-1" /> {day.location}
                  </div>
                )}
                {day.description && (
                  <p className="text-muted-foreground">{day.description}</p>
                )}
              </div>
            ))
          ) : (
            <p className="text-center text-muted-foreground">No day-by-day details available.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ItineraryDetails;
